import { createContext, useContext, useState, useEffect } from 'react'
import { usePayment } from './PaymentContext'
import {
  getSession,
  completeSession as _completeSession,
  startSessionOtpVerification,
  updateSession,
  completeSessionOtpVerification,
  validateAccountNumber,
  initiateMandate as _initiateMandate,
} from '../services/SessionService'
import { SessionStatus } from '../constants/SessionConstants'


// // Session operations interface for different funding sources
// export const SessionOperations = {
//   LOAD: 'load',
//   UPDATE: 'update',
//   COMPLETE: 'complete',
//   START_OTP: 'start_otp',
//   COMPLETE_OTP: 'complete_otp',
//   VALIDATE: 'validate',
// }

export const SessionContext = createContext(null)

export const SessionContextProvider = ({ children }) => {
  const [sessionId, setSessionId] = useState(null)
  const [sessionData, setSessionData] = useState(null)
  const [currentFundingSource, setCurrentFundingSource] = useState(null)
  const [fundingSourceStatus, setFundingSourceStatus] = useState(
    SessionStatus.INITIALIZED
  )
  const [fundingSourceData, setFundingSourceData] = useState({})
  const [sessionError, setSessionError] = useState(null)
  const [sessionTimeout, setSessionTimeout] = useState(null)
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null)

  // Cleanup function to clear timeouts
  useEffect(() => {
    return () => {
      if (sessionTimeout) {
        clearTimeout(sessionTimeout)
      }
    }
  }, [sessionTimeout])

  // Load session data from API
  const loadSessionData = async (sessionId) => {
    try {
      const response = await getSession(sessionId)
      setSessionData(response.data)

      return response
    } catch (error) {
      setSessionError(error.message)
      console.error('Error loading session data:', error)
    }
  }

  // Update session data
  const updateSessionData = async (sessionId, data) => {
    try {
      const response = await updateSession(sessionId, data)
      setSessionData((prev) => ({ ...prev, ...response.data }))
      setFundingSourceData((prev) => ({ ...prev, ...data }))

      return response
    } catch (error) {
      setSessionError(error.message)
      console.error('Error updating session data:', error)
      throw error
    }
  }

  // Complete session
  const completeSession = async (sessionId) => {
    try {
      const response = await _completeSession(sessionId)
      setFundingSourceStatus(SessionStatus.SUCCESS)

      return response
    } catch (error) {
      setFundingSourceStatus(SessionStatus.FAILED)
      setSessionError(error.message)
      console.error('Error completing session:', error)
      throw error
    }
  }

  const startOtpVerification = async (sessionId, verificationData) => {
    try {
      const otpData = {
        ...verificationData,
        sessionKey: sessionId,
      }
      const response = await startSessionOtpVerification(otpData)
      setFundingSourceStatus(SessionStatus.OTP_VERIFICATION)
      
      return response
    } catch (error) {
      setSessionError(error.message)
      console.error('Error starting OTP verification:', error)
      throw error
    }
  }

  const completeOtpVerification = async (sessionId, otpData) => {
    try {
      // Prepare the data for the new API format
      const verificationData = {
        ...otpData,
        sessionKey: sessionId, // Ensure session key is included
      }

      const response = await completeSessionOtpVerification(verificationData)

      setFundingSourceStatus(SessionStatus.CONFIRMATION)
      return response
    } catch (error) {
      setSessionError(error.message)
      console.error('Error completing OTP verification:', error)
      throw error
    }
  }

  // Validate account number
  const validateAccountDetails = async (sessionId, validationData) => {
    try {
      const response = await validateAccountNumber(sessionId, validationData, currentFundingSource)
      return response
    } catch (error) {
      setSessionError(error.message)
      console.error('Error validating account:', error)
      throw error
    }
  }

  const initiateMandate = async (sessionId, mandateData) => {
    try {
      const response = await _initiateMandate(sessionId, currentFundingSource, mandateData)
      setFundingSourceData((prev) => ({ ...prev, ...mandateData }))
      return response
    } catch (error) {
      setSessionError(error.message)
      console.error('Error initiating mandate:', error)
      throw error
    }
  }

  const stopSessionTimer = () => {
    if (sessionTimeout) {
      clearTimeout(sessionTimeout)
      setSessionTimeout(null)
    }
  }

  // Reset session
  const resetSession = () => {
    if (sessionTimeout) {
      clearTimeout(sessionTimeout)
      setSessionTimeout(null)
    }
    setSessionExpiresAt(null)
    setSessionData(null)
    setCurrentFundingSource(null)
    setFundingSourceStatus(SessionStatus.INITIALIZED)
    setFundingSourceData({})
    setSessionError(null)
  }

  // Set session timeout
  const setSessionTimeoutHandler = (timeoutMs = 600000) => {
    // 10 minutes default
    if (sessionTimeout) {
      clearTimeout(sessionTimeout)
    }

    // On elapse, defer the expiry decision to the backend: re-fetch the session
    // status. The status mapping expires the flow only if the backend actually
    // reports it expired; if the backend still considers it active, nothing happens.
    const newTimeout = setTimeout(() => {
      loadSessionData(sessionId)
    }, timeoutMs)

    setSessionTimeout(newTimeout)
  }

  useEffect(() => {
    if (!sessionData?.expiresAt) return

    setSessionExpiresAt(sessionData.expiresAt)
    const timeoutMs = new Date(sessionData.expiresAt).getTime() - Date.now()
    setSessionTimeoutHandler(Math.max(0, timeoutMs))
  }, [sessionData?.expiresAt])

  // Extend session timeout
  const extendSessionTimeout = (timeoutMs = 600000) => {
    // 10 minutes default
    setSessionTimeoutHandler(timeoutMs)
  }

  // // Generic operation handler that routes to appropriate method based on funding source
  // const executeSessionOperation = async (operation, sessionId, data = {}) => {
  //   switch (operation) {
  //     case SessionOperations.LOAD:
  //       return await loadSessionData(sessionId)
  //     case SessionOperations.UPDATE:
  //       return await updateSessionData(sessionId, data)
  //     case SessionOperations.COMPLETE:
  //       return await completeSession(sessionId)
  //     case SessionOperations.START_OTP:
  //       return await startOtpVerification(sessionId, data)
  //     case SessionOperations.COMPLETE_OTP:
  //       return await completeOtpVerification(sessionId, data)
  //     case SessionOperations.VALIDATE:
  //       return await validateAccountDetails(data)
  //     default:
  //       throw new Error(`Unknown session operation: ${operation}`)
  //   }
  // }

  // Context value
  const value = {
    sessionId,
    setSessionId,
    sessionData,
    currentFundingSource,
    fundingSourceStatus,
    fundingSourceData,
    sessionError,
    sessionTimeout,
    sessionExpiresAt,
    loadSessionData,
    updateSessionData,
    completeSession,
    startOtpVerification,
    completeOtpVerification,
    validateAccountDetails,
    initiateMandate,
    resetSession,
    stopSessionTimer,
    setFundingSourceStatus,
    setFundingSourceData,
    setCurrentFundingSource,
    setSessionTimeoutHandler,
    extendSessionTimeout,
    // executeSessionOperation,
    // SessionOperations,
  }

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  )
}

export const useSession = () => {
  const context = useContext(SessionContext)
  if (!context) {
    throw new Error('useSession must be used within a SessionContextProvider')
  }
  return context
}
